import { useEffect, useRef, useState } from "react";
import {
  Box,
  TextField,
  Divider,
  Autocomplete,
  IconButton,
} from "@mui/material";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { useDrag, useDrop, XYCoord } from "react-dnd";

export default function SubQuestions({
  question,
  index,
  definedNames,
  usedDefinedNames,
  allQuestions,
  submitCount,
  onDelete,
  onUpdate,
  moveCard,
}: any) {
  const ref = useRef<HTMLDivElement>(null);
  const [selectedDefinedNames, setSelectedDefinedNames] = useState<string[]>(
    []
  );

  useEffect(() => {
    if (allQuestions && allQuestions.length > 0) {
      const definedNames = [...usedDefinedNames];
      allQuestions.forEach((question: any) => {
        if (question?.definedName) {
          definedNames.push(question?.definedName);
        }
      });
      setSelectedDefinedNames(definedNames);
    }
  }, [allQuestions, usedDefinedNames]);

  const [{ handlerId }, drop] = useDrop(() => ({
    accept: "CARD",
    drop: (item: any) => {},
    collect: (monitor: any) => ({
      handlerId: monitor.getHandlerId(),
    }),
    hover(item: any, monitor) {
      if (!ref.current) {
        return;
      }

      if (item?.card) {
        // if new section is dragged then don't perform reorder
        return;
      }
      const dragIndex = item?.index;
      const hoverIndex = index;

      // Don't replace items with themselves
      if (dragIndex === hoverIndex) {
        return;
      }

      // Determine rectangle on screen
      const hoverBoundingRect = ref.current?.getBoundingClientRect();

      // Get vertical middle
      const hoverMiddleY =
        (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;

      // Determine mouse position
      const clientOffset = monitor.getClientOffset();

      // Get pixels to the top
      const hoverClientY = (clientOffset as XYCoord).y - hoverBoundingRect.top;
      // Dragging downwards
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return;
      }
      // Dragging upwards
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return;
      }
      moveCard(dragIndex, hoverIndex, question);
      item.index = hoverIndex;
    },
  }));

  const [{ opacity }, drag] = useDrag({
    type: "CARD",
    item: () => {
      return { question, index };
    },
    collect: (monitor: any) => ({
      opacity: monitor.isDragging() ? 0.7 : 1,
    }),
  });
  drag(drop(ref));

  return (
    <Box ref={ref} data-handler-id={handlerId}>
      <Box display={"flex"} alignItems={"flex-start"}>
        <Box sx={{ width: "calc(100% - 96px)", opacity: opacity }}>
          <TextField
            sx={{ ml: 2 }}
            fullWidth
            id="title"
            name="title"
            variant="outlined"
            label="Sub Question"
            value={question.title}
            onChange={(e) => onUpdate(index, e.target.value)}
            error={!question.title && submitCount > 0}
            helperText={
              !question.title && submitCount > 0 && "This field can't be empty"
            }
          />
          <Autocomplete
            fullWidth
            sx={{ mt: 2, ml: 2 }}
            disablePortal
            options={definedNames}
            value={question.definedName}
            getOptionDisabled={(option) =>
              selectedDefinedNames.includes(option)
            }
            onChange={(event, newValue) => {
              onUpdate(index, question.title, newValue);
            }}
            renderInput={(params) => (
              <TextField
                name="definedName"
                {...params}
                label="Defined Name"
                error={!question.definedName && submitCount > 0}
              />
            )}
          />
        </Box>
        <IconButton
          aria-label="delete"
          sx={{ ml: 3 }}
          color="error"
          onClick={() => onDelete(index)}
        >
          <DeleteOutlinedIcon />
        </IconButton>
        <IconButton aria-label="drag">
          <DragIndicatorIcon />
        </IconButton>
      </Box>
      <Divider sx={{ my: 2 }} />
    </Box>
  );
}
